'use client';

import React from 'react';

import {
    Alignable,
    Box,
    Column,
    Columns,
    Divider,
    Icon,
    IconColor,
    IconName,
    Justifiable,
    List,
    ListItem,
    Spacer,
    SpacerSize,
    Text,
    TextMarkup,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyAlign,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { NumeriquesReviewInformation } from '@app-types/numeriques';

export type PartnerProps = {
    partnerData?: NumeriquesReviewInformation['publications'][number];
};

export function Partner({ partnerData }: PartnerProps) {
    if (!partnerData) return null;

    const pluses = typeof partnerData.pluses === 'string' ? partnerData.pluses.split(',') : partnerData.pluses;
    const minuses = typeof partnerData.minuses === 'string' ? partnerData.minuses.split(',') : partnerData.minuses;

    return (
        <div id='partner-section' className='ignore-sticky-scroll'>
            <Columns multiline>
                <Column verticalAlign={Alignable.ALIGNED_CENTER} tabletSize={12} desktopSize={3} desktopOffset={1}>
                    <img
                        src='https://cdn.lesnumeriques.com/assets/2024/04/30/1714489863461/newebfrontstyleguide/build/img/svg/logo-horizontal.svg'
                        alt='Les Numériques'
                        width='200px'
                        loading='lazy'
                    />
                    <Spacer size={SpacerSize.SIX} />
                    <Text typo={TypographyAlign.TEXT_LEFT}>
                        <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Lesnumériques.com{' '}
                        </Text>
                        <Text markup={TextMarkup.SPAN}>
                            est le site dédié aux tests et comparatifs de produits tech. Ils nous partagent leurs avis
                            d&apos;experts pour vous guider dans le choix de votre téléphone.
                        </Text>
                    </Text>
                </Column>
                <Column verticalAlign={Alignable.ALIGNED_CENTER} tabletSize={12} desktopSize={3} desktopOffset={1}>
                    <Title level={TitleLevels.FIVE} typo={TypographyAlign.TEXT_LEFT}>
                        L&apos;avis de la rédaction
                    </Title>
                    {Object.entries(partnerData.subnotes).map(([text, value]) => {
                        if (!value) return null;
                        return (
                            <View key={text} flexable justify={Justifiable.SPACE_BETWEEN}>
                                <Text marginless>{text}</Text>
                                <Text className='is-flex'>
                                    <Icon name={IconName.STAR_FILLED} className='numeriques-star' />
                                    {value}
                                </Text>
                            </View>
                        );
                    })}
                    <Divider />
                    <View flexable justify={Justifiable.SPACE_BETWEEN}>
                        <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>Moyenne générale</Text>
                        <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} className='is-flex'>
                            <Icon name={IconName.STAR_FILLED} className='numeriques-star' />
                            {partnerData.note}
                        </Text>
                    </View>
                </Column>
                <Column verticalAlign={Alignable.ALIGNED_CENTER} tabletSize={12} desktopSize={3} desktopOffset={1}>
                    <Box shadowless backgroundColor={TrilogyColor.NEUTRAL_FADE} className='has-text-left'>
                        <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} className='is-flex is-aligned-center'>
                            <Icon name={IconName.CHECK_CIRCLE} color={IconColor.SUCCESS} />
                            &nbsp;Points forts
                        </Text>
                        <List className='p-small'>
                            {pluses.map((plus) => (
                                <ListItem key={plus}>{plus}</ListItem>
                            ))}
                        </List>
                        <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} className='is-flex is-aligned-center'>
                            <Icon name={IconName.MINUS_CIRCLE} color={IconColor.ERROR} />
                            &nbsp;Points faibles
                        </Text>
                        <List className='p-small'>
                            {minuses.map((minus) => (
                                <ListItem key={minus}>{minus}</ListItem>
                            ))}
                        </List>
                    </Box>
                </Column>
            </Columns>
        </div>
    );
}
